<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, ref, watch, onMounted } from 'vue'
import { useLogger } from 'vue-logger-plugin'
import { TextBox, Checkbox } from '@progress/kendo-vue-inputs'
import { DropDownList } from '@progress/kendo-vue-dropdowns'
import { GetWebConnectorUrl } from '../../stores/SettingsStore.ts'
import { ApiPut } from '../../api/Common.ts'

const { t } = useI18n()
const logger = useLogger()

// Props defined explicitly to avoid TypeScript errors
import type { IWizardStepProps } from '../../types/WizardTypes'
import { FrenchContributionNUWUpdate } from '@/api/Business.ts'

const props = defineProps<IWizardStepProps & {
  validationError?: string
}>()

// Emit events to parent component
const emit = defineEmits(['update:isValid', 'clearValidationError'])

// State for handling loading and errors
const isLoading = ref(false)
const loadError = ref('')
const submitError = ref('')

// Form data for French contribution - use the data from the parent component
const frenchContribution = computed(() => props.formData.frenchContribution)

// Local reactive state for dropdowns to fix display issues
const localEmployeeStatus = ref(props.formData.frenchContribution?.employeeStatus || '')
const localBirthCountry = ref(props.formData.frenchContribution?.birthCountry || '')
const localVatType = ref(props.formData.frenchContribution?.codeIdExternalTaxId || '')

// Employee status options
const employeeStatusOptions = [
  { value: 'RCS', labelKey: 'labels.options.RCS', text: t('labels.options.RCS') },
  { value: 'VDI', labelKey: 'labels.options.VDI', text: t('labels.options.VDI') }
]

// VAT type options
const vatTypeOptions = [
  { value: 'VAT', labelKey: 'labels.options.VAT', text: t('labels.options.VAT') },
  { value: 'TAXID', labelKey: 'labels.options.TAXID', text: t('labels.options.TAXID') }
]

// Get the current VAT type value (handle both object and string)
const currentVatType = computed(() => {
  return typeof localVatType.value === 'object' && localVatType.value !== null
    ? (localVatType.value as any).value
    : localVatType.value
})

// Country options - this would typically be loaded from an API
// For now, we'll include a few common countries
const countryOptions = [
  { value: 'France', labelKey: 'labels.options.France', text: t('labels.options.France') },
  { value: 'Belgium', labelKey: 'labels.options.Belgique', text: t('labels.options.Belgique') },
  { value: 'Switzerland', labelKey: 'labels.options.Suisse', text: t('labels.options.Suisse') },
  { value: 'Germany', labelKey: 'labels.options.Allemagne', text: t('labels.options.Allemagne') },
  { value: 'Spain', labelKey: 'labels.options.Espagne', text: t('labels.options.Espagne') },
  { value: 'United Kingdom', labelKey: 'labels.options.RoyaumeUni', text: t('labels.options.RoyaumeUni') },
  { value: 'United States', labelKey: 'labels.options.EtatsUnis', text: t('labels.options.EtatsUnis') },
  { value: 'Canada', labelKey: 'labels.options.Canada', text: t('labels.options.Canada') }
]

// Validation for SSN (French Social Security Number)
const isValidSSN = computed(() => {
  const ssn = frenchContribution.value.ssn
  // Basic validation: must be 15 digits
  return /^\d{15}$/.test(ssn)
})

// Show SIRET warning if empty
const showSiretWarning = computed(() => {
  return frenchContribution.value.siret.trim() === ''
})

// Validation for VAT number (French format: FR + 11 characters)
const isValidVATNumber = computed(() => {
  const vatNumber = frenchContribution.value.taxExemptionCode

  // If TAXID is selected, VAT number is not required - always valid
  if (currentVatType.value === 'TAXID') {
    return true
  }

  // If VAT is selected, the field is required and must match format
  if (!vatNumber || vatNumber.trim() === '') {
    return false
  }

  // French VAT format: FR followed by 11 characters (2 check digits + 9 SIREN digits)
  return /^FR[0-9A-Z]{11}$/i.test(vatNumber.trim())
})

// Determine if the step is valid
const isStepValid = computed(() => {
  const { birthCity, birthCountry, employeeStatus, ssn, siret, taxExemptionCode, codeIdExternalTaxId } = frenchContribution.value

  // Log raw values for debugging
  logger.debug('Raw form values:', {
    birthCity,
    birthCountry,
    employeeStatus,
    ssn,
    siret,
    frenchCitizen: frenchContribution.value.frenchCitizen,
    authorizedToWork: frenchContribution.value.authorizedToWork,
    taxExemptionCode,
    codeIdExternalTaxId,
    currentVatType: currentVatType.value
  })

  // Required fields validation - all must be filled (SIRET is optional)
  // VAT number is only required if VAT type is 'VAT', not required for 'TAXID'
  const requiredFieldsValid =
    birthCity.trim() !== '' &&
    birthCountry.trim() !== '' &&
    employeeStatus.trim() !== '' &&
    ssn.trim() !== '' &&
    codeIdExternalTaxId.trim() !== '' &&
    (currentVatType.value === 'TAXID' || taxExemptionCode.trim() !== '')

  // For SSN, if provided, verify it has 15 digits
  const ssnValid = ssn.trim() === '' || isValidSSN.value

  // For work authorization, must be French citizen OR authorized to work
  const authorizedToWorkValid =
    frenchContribution.value.frenchCitizen ||
    frenchContribution.value.authorizedToWork

  // VAT number validation (automatically valid if TAXID is selected)
  const vatNumberValid = isValidVATNumber.value

  // The step is valid if all required fields are filled, SSN is valid, authorized to work, VAT is valid, AND no validation errors
  const isValid = requiredFieldsValid && ssnValid && authorizedToWorkValid && vatNumberValid && !props.validationError

  // Log validation state for debugging
  logger.debug('Form validation state:', {
    requiredFieldsValid,
    birthCity: birthCity.trim() !== '',
    birthCountry: birthCountry.trim() !== '',
    employeeStatus: employeeStatus.trim() !== '',
    ssn: ssn.trim() !== '',
    taxExemptionCode: taxExemptionCode.trim() !== '',
    codeIdExternalTaxId: codeIdExternalTaxId.trim() !== '',
    currentVatType: currentVatType.value,
    vatTypeIsTaxId: currentVatType.value === 'TAXID',
    ssnValid,
    authorizedToWorkValid,
    vatNumberValid,
    hasValidationError: !!props.validationError,
    isValid
  })

  return isValid
})

// Update the validity of the step when it changes
watch(isStepValid, (newValue) => {
  logger.debug('Emitting isValid update from watch:', newValue)
  updateValidityState(newValue)
})

// Explicit method to force validation state update
function updateValidityState(isValid: boolean) {
  logger.debug('Forcing validity state update:', isValid)
  emit('update:isValid', isValid)
}

// Explicit method to force complete form validation
function forceValidation() {
  // Check current validation state
  const currentValidState = isStepValid.value
  logger.debug('Forcing validation check, current state:', currentValidState)

  // Emit validation event to parent component
  emit('update:isValid', currentValidState)

  // For debugging, show current form values
  logger.debug('Current form values:', {
    employeeStatus: frenchContribution.value.employeeStatus,
    birthCity: frenchContribution.value.birthCity,
    birthCountry: frenchContribution.value.birthCountry,
    ssn: frenchContribution.value.ssn,
    siret: frenchContribution.value.siret,
    frenchCitizen: frenchContribution.value.frenchCitizen,
    authorizedToWork: frenchContribution.value.authorizedToWork
  })

  // Force a second emission after a brief delay to ensure the parent component receives it
  setTimeout(() => {
    logger.debug('Re-emitting validation state after delay:', currentValidState)
    emit('update:isValid', currentValidState)
  }, 50)

  return currentValidState
}

// Initialize the form with default data if necessary
function initializeFormData() {
  // If there are no initial values, set default values to avoid errors
  if (!frenchContribution.value.employeeStatus) {
    props.onUpdate({
      frenchContribution: {
        ...frenchContribution.value,
        employeeStatus: ''
      }
    })
  }

  if (!frenchContribution.value.birthCountry) {
    props.onUpdate({
      frenchContribution: {
        ...frenchContribution.value,
        birthCountry: ''
      }
    })
  }

  // Force validation after initialization
  forceValidation()
}

// Watch local state and update parent
watch([localEmployeeStatus, localBirthCountry, localVatType], () => {
  // Update parent component when local state changes
  const employeeStatusValue = typeof localEmployeeStatus.value === 'object' && localEmployeeStatus.value !== null
    ? (localEmployeeStatus.value as any).value
    : localEmployeeStatus.value

  const birthCountryValue = typeof localBirthCountry.value === 'object' && localBirthCountry.value !== null
    ? (localBirthCountry.value as any).value
    : localBirthCountry.value

  const vatTypeValue = typeof localVatType.value === 'object' && localVatType.value !== null
    ? (localVatType.value as any).value
    : localVatType.value

  // Clear VAT number when TAXID is selected
  const updatedTaxExemptionCode = vatTypeValue === 'TAXID' ? '' : frenchContribution.value.taxExemptionCode

  props.onUpdate({
    frenchContribution: {
      ...frenchContribution.value,
      employeeStatus: employeeStatusValue,
      birthCountry: birthCountryValue,
      codeIdExternalTaxId: vatTypeValue,
      taxExemptionCode: updatedTaxExemptionCode
    }
  })

  forceValidation()
})

// Initialize the form with data from parent if available
onMounted(() => {
  logger.debug('FrenchContributionStep mounted', frenchContribution.value)
  logger.debug('Initial validation state:', isStepValid.value)

  // Initialize local state with current values
  localEmployeeStatus.value = frenchContribution.value.employeeStatus || ''
  localBirthCountry.value = frenchContribution.value.birthCountry || ''
  localVatType.value = frenchContribution.value.codeIdExternalTaxId || ''

  // Initialize form data
  initializeFormData()

  // Force initial validation
  forceValidation()

  // Schedule additional validations to ensure the parent component receives the correct state
  setTimeout(() => {
    forceValidation()
  }, 100)

  setTimeout(() => {
    forceValidation()
  }, 300)
})

// Handle employee status change
function handleEmployeeStatusChange(event: any) {
  if (event && event.value !== undefined) {
    const value = event.value;
    localEmployeeStatus.value = value
    // Clear validation error when user makes changes
    if (props.validationError) {
      clearValidationError()
    }
    // Debug to verify the selected value
    logger.debug('Employee Status selected:', value)
  }
}

// Handle birth country change
function handleBirthCountryChange(event: any) {
  if (event && event.value !== undefined) {
    const value = event.value;
    localBirthCountry.value = value
    // Clear validation error when user makes changes
    if (props.validationError) {
      clearValidationError()
    }
    // Debug to verify the selected value
    logger.debug('Birth Country selected:', value)
  }
}

// Handle birth city change
function handleBirthCityChange(...args: any[]) {
  const event = args[0];
  if (event && event.target && event.target.value !== undefined) {
    const value = event.target.value;
    // Clear validation error when user makes changes
    if (props.validationError) {
      clearValidationError()
    }
    props.onUpdate({
      frenchContribution: {
        ...frenchContribution.value,
        birthCity: value
      }
    })
    // Force complete form validation
    forceValidation()
    // Debug to verify the entered value
    logger.debug('Birth City entered:', value)
  }
}

// Handle SSN change
function handleSSNChange(...args: any[]) {
  const event = args[0];
  if (event && event.target && event.target.value !== undefined) {
    // Clear validation error when user makes changes
    if (props.validationError) {
      clearValidationError()
    }
    // Only allow digits
    const digitsOnly = event.target.value.replace(/\D/g, '')
    props.onUpdate({
      frenchContribution: {
        ...frenchContribution.value,
        ssn: digitsOnly
      }
    })
    // Force complete form validation
    forceValidation()
    // Debug to verify the entered value
    logger.debug('SSN entered (digits only):', digitsOnly)
  }
}

// Handle SIRET change
function handleSIRETChange(...args: any[]) {
  const event = args[0];
  if (event && event.target && event.target.value !== undefined) {
    const value = event.target.value;
    // Clear validation error when user makes changes
    if (props.validationError) {
      clearValidationError()
    }
    props.onUpdate({
      frenchContribution: {
        ...frenchContribution.value,
        siret: value
      }
    })
    // Force complete form validation
    forceValidation()
    // Debug to verify the entered value
    logger.debug('SIRET entered:', value)
  }
}

// Handle French citizen checkbox change
function handleFrenchCitizenChange(...args: any[]) {
  const event = args[0];
  const isChecked = event.value;
  logger.debug('French citizen checkbox changed:', isChecked);

  // If French citizen is checked, reset authorized to work
  const authorizedToWork = isChecked ? false : frenchContribution.value.authorizedToWork;

  props.onUpdate({
    frenchContribution: {
      ...frenchContribution.value,
      frenchCitizen: isChecked,
      authorizedToWork
    }
  })
  // Forzar la validación completa del formulario
  forceValidation()
}

// Handle authorized to work checkbox change
function handleAuthorizedToWorkChange(...args: any[]) {
  const event = args[0];
  const isChecked = event.value;
  logger.debug('Authorized to work checkbox changed:', isChecked);

  props.onUpdate({
    frenchContribution: {
      ...frenchContribution.value,
      authorizedToWork: isChecked
    }
  })
  // Forzar la validación completa del formulario
  forceValidation()
}

// Handle VAT type change
function handleVatTypeChange(event: any) {
  if (event && event.value !== undefined) {
    const value = event.value;
    localVatType.value = value
    // Clear validation error when user makes changes
    if (props.validationError) {
      clearValidationError()
    }
    // Debug to verify the selected value
    logger.debug('VAT Type selected:', value)
  }
}

// Handle VAT number change
function handleVatNumberChange(...args: any[]) {
  const event = args[0];
  if (event && event.target && event.target.value !== undefined) {
    const value = event.target.value;
    // Clear validation error when user makes changes
    if (props.validationError) {
      clearValidationError()
    }
    props.onUpdate({
      frenchContribution: {
        ...frenchContribution.value,
        taxExemptionCode: value
      }
    })
    // Force validation
    forceValidation()
    // Debug to verify the entered value
    logger.debug('VAT Number entered:', value)
  }
}

// Submit French contribution data
async function submitFrenchContribution() {
  try {
    isLoading.value = true
    submitError.value = ''

    // Prepare the request data
    const requestData = {
      BeeNumber: frenchContribution.value.beeNumber,
      BirthCity: frenchContribution.value.birthCity,
      BirthCountry: frenchContribution.value.birthCountry,
      FrenchCitizen: frenchContribution.value.frenchCitizen,
      AuthorizedToWork: frenchContribution.value.authorizedToWork,
      EmployeeStatus: frenchContribution.value.employeeStatus,
      SSN: frenchContribution.value.ssn,
      SIRET: frenchContribution.value.siret
    }

    // Make the API call
    const response = await FrenchContributionNUWUpdate(requestData)

    logger.info('French contribution data submitted:', response)
    return response

  } catch (error) {
    logger.error('Error submitting French contribution data:', error)
    submitError.value = t('errors.savingFrenchContribution')
    throw error
  } finally {
    isLoading.value = false
  }
}

// Clear validation error function
function clearValidationError() {
  emit('clearValidationError')
}

// Export the submit function to be called from the parent component
defineExpose({
  submitFrenchContribution
})
</script>

<template>
  <div class="wizard-step">
    <div class="wizard-step-header">
      <h3>{{ t('labels.FrenchContributionHeader') }}</h3>
      <p>{{ t('labels.FrenchContributionSubHeader') }}</p>
    </div>

    <div class="form-section">
      <div v-if="isLoading" class="loading-indicator">
        {{ t('labels.LoadingFrenchContribution') }}
      </div>

      <div v-else-if="loadError" class="validation-message">
        {{ loadError }}
      </div>

      <div v-else>
        <!-- Employee Status -->
        <div class="form-row">
          <label class="form-label">{{ t('labels.EmployeeStatus') }} *</label>
          <DropDownList
            :data-items="employeeStatusOptions"
            v-model="localEmployeeStatus"
            :text-field="'text'"
            :value-field="'value'"
            @change="handleEmployeeStatusChange"
            class="k-dropdown"
            :default-item="{ text: t('labels.SelectOption'), value: '' }"
          />
          <div v-if="frenchContribution.employeeStatus === ''" class="validation-message">
            {{ t('validators.requiredField', { fieldName: t('labels.EmployeeStatus') }) }}
          </div>
        </div>

        <!-- SSN (Social Security Number) -->
        <div class="form-row">
          <label class="form-label">{{ t('labels.SSN') }} *</label>
          <TextBox
            v-model="frenchContribution.ssn"
            class="k-textbox"
            :placeholder="t('labels.SSNPlaceholder')"
            @change="handleSSNChange"
          />
          <div v-if="frenchContribution.ssn === ''" class="validation-message">
            {{ t('validators.requiredField', { fieldName: t('labels.SSN') }) }}
          </div>
          <div v-else-if="!isValidSSN" class="validation-message">
            {{ t('validators.formatField', { fieldName: t('labels.SSN') }) }}
          </div>
        </div>

        <!-- SIRET Number -->
        <div class="form-row">
          <label class="form-label">{{ t('labels.SIRET') }}</label>
          <TextBox
            v-model="frenchContribution.siret"
            class="k-textbox"
            :placeholder="t('labels.SIRETPlaceholder')"
            @change="handleSIRETChange"
          />
          <div v-if="showSiretWarning" class="warning-message">
            {{ t('validators.siretWarning') }}
          </div>
        </div>

        <!-- Birth City -->
        <div class="form-row">
          <label class="form-label">{{ t('labels.BirthCity') }} *</label>
          <TextBox
            v-model="frenchContribution.birthCity"
            class="k-textbox"
            :placeholder="t('labels.BirthCityPlaceholder')"
            @change="handleBirthCityChange"
          />
          <div v-if="frenchContribution.birthCity === ''" class="validation-message">
            {{ t('validators.requiredField', { fieldName: t('labels.BirthCity') }) }}
          </div>
        </div>

        <!-- Birth Country -->
        <div class="form-row">
          <label class="form-label">{{ t('labels.BirthCountry') }} *</label>
          <DropDownList
            :data-items="countryOptions"
            v-model="localBirthCountry"
            :text-field="'text'"
            :value-field="'value'"
            @change="handleBirthCountryChange"
            class="k-dropdown"
            :default-item="{ text: t('labels.SelectOption'), value: '' }"
          />
          <div v-if="frenchContribution.birthCountry === ''" class="validation-message">
            {{ t('validators.requiredField', { fieldName: t('labels.BirthCountry') }) }}
          </div>
        </div>

        <!-- VAT Type -->
        <div class="form-row">
          <label class="form-label">{{ t('labels.VatType') }} *</label>
          <DropDownList
            :data-items="vatTypeOptions"
            v-model="localVatType"
            :text-field="'text'"
            :value-field="'value'"
            @change="handleVatTypeChange"
            class="k-dropdown"
            :default-item="{ text: t('labels.SelectOption'), value: '' }"
          />
        </div>

        <!-- VAT Number / Tax Exemption Code -->
        <div class="form-row">
          <label class="form-label">{{ t('labels.VatNumber') }} <span v-if="currentVatType !== 'TAXID'">*</span></label>
          <TextBox
            v-model="frenchContribution.taxExemptionCode"
            class="k-textbox"
            :placeholder="t('labels.VatNumberPlaceholder')"
            :class="{ 'invalid-input': frenchContribution.taxExemptionCode !== '' && !isValidVATNumber }"
            :disabled="currentVatType === 'TAXID'"
            @change="handleVatNumberChange"
          />
          <div v-if="currentVatType !== 'TAXID' && frenchContribution.taxExemptionCode === ''" class="validation-message">
            {{ t('validators.requiredField', { fieldName: t('labels.VatNumber') }) }}
          </div>
          <div v-else-if="currentVatType !== 'TAXID' && !isValidVATNumber" class="validation-message">
            {{ t('validators.vatNumberFormat') }}
          </div>
        </div>

        <!-- French Citizen -->
        <div class="form-row checkbox-row">
          <Checkbox
            v-model="frenchContribution.frenchCitizen"
            :label="t('labels.FrenchCitizen')"
            @change="handleFrenchCitizenChange"
          />
        </div>

        <!-- Authorized to Work (only visible if not French citizen) -->
        <div v-if="!frenchContribution.frenchCitizen" class="form-row checkbox-row">
          <Checkbox
            v-model="frenchContribution.authorizedToWork"
            :label="t('labels.AuthorizedToWork')"
            @change="handleAuthorizedToWorkChange"
          />
          <div v-if="!frenchContribution.authorizedToWork" class="validation-message">
            {{ t('validators.authorizedToWorkRequired') }}
          </div>
        </div>

        <!-- Submit Error Message -->
        <div v-if="submitError" class="validation-message error-message">
          {{ submitError }}
        </div>

        <!-- Validation Error Alert - appears at bottom of form -->
        <div v-if="props.validationError" class="form-validation-alert">
          <div class="alert-content">
            <div class="alert-message">{{ props.validationError }}</div>
            <button @click="clearValidationError" class="alert-close" type="button">×</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.wizard-step {
  margin-bottom: 25px;
}

.wizard-step-header {
  margin-bottom: 16px;
}

.wizard-step-header h3 {
  @include typography-h4;
  @include font-weight-semibold;
  margin-bottom: 6px;
}

.wizard-step-header p {
  @include typography-body;
  color: #666;
  margin-bottom: 12px;
}

.form-section {
  margin-bottom: 25px;
}

.form-row {
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
}

.checkbox-row {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.form-label {
  @include typography-body;
  @include font-weight-medium;
  display: block;
  margin-bottom: 6px;
  min-width: 120px;
}

.k-textbox {
  @include typography-body;
  height: 34px;
}

.k-dropdown {
  @include typography-body;
}

/* Ensure Kendo UI components have consistent height and width */
:deep(.k-dropdown),
:deep(.k-textbox) {
  @include typography-body;
  width: 100%;
  height: 34px;
}

:deep(.k-dropdown .k-dropdown-wrap) {
  height: 34px;
}

:deep(.k-dropdown .k-input) {
  height: 32px;
  line-height: 32px;
}

.help-text {
  @include typography-small;
  color: #666;
  margin-top: 4px;
}

.k-checkbox-label {
  @include typography-body;
}

.validation-message {
  @include typography-small;
  color: #e53935;
  margin-top: 4px;
}

.warning-message {
  @include typography-small;
  color: #ff9800;
  margin-top: 4px;
}

.error-message {
  margin-top: 12px;
  padding: $spacing-2;
  @include typography-small;
  background-color: rgba(#e53935, 0.1);
  border-radius: $radius-sm;
}

.loading-indicator {
  @include typography-body;
  margin: $spacing-4 0;
  font-style: italic;
  color: #666;
}

.form-validation-alert {
  margin-top: 20px;
  margin-bottom: 10px;
  border-radius: $radius-md;
  background-color: #ffff5f5;
  border: 1px solid #fecaca;
  box-shadow: 0 2px 4px rgba(black, 0.1);
}

.alert-content {
  display: flex;
  align-items: center;
  padding: $spacing-3 $spacing-4;
  gap: $spacing-3;
}

.alert-icon {
  @include typography-h4;
  flex-shrink: 0;
}

.alert-message {
  @include typography-body;
  @include font-weight-medium;
  flex: 1;
  color: #dc2626;
}

.alert-close {
  background: none;
  @include typography-h3;
  @include font-weight-bold;
  border: none;
  color: #dc2626;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: $radius-sm;
  transition: background-color 0.2s;
}

.alert-close:hover {
  background-color: rgba(#dc2626, 0.1);
}

/* Style for invalid input */
.invalid-input {
  border-color: #e53935 !important;
}

:deep(.k-textbox.invalid-input) {
  border-color: #e53935 !important;
}
</style>
